/**
 * Toast Helper Utility
 * Provides toast notification wrappers for service calls
 * This is a client-side only utility - import dynamically in client components
 */

import { getErrorMessage } from './errorHandler';

let toastInstance = null;

/**
 * Initialize toast helper with toast context
 * Call this from a client component that has access to useToast
 */
export const initToastHelper = (toast) => {
  toastInstance = toast;
};

/**
 * Get toast instance (for direct usage)
 */
export const getToast = () => toastInstance;

/**
 * Wrapper for service calls with automatic toast notifications
 * @param {Function} serviceCall - The service function to call
 * @param {Object} options - Toast options
 * @param {string} options.successMessage - Message to show on success
 * @param {string} options.errorMessage - Message to show on error (optional, uses API error by default)
 * @param {boolean} options.showSuccess - Whether to show success toast (default: true)
 * @param {boolean} options.showError - Whether to show error toast (default: true)
 * @param {number} options.duration - Toast duration in ms (default: 4000)
 */
export const withToast = async (serviceCall, options = {}) => {
  const {
    successMessage,
    errorMessage,
    showSuccess = true,
    showError = true,
    duration = 4000
  } = options;

  try {
    const result = await serviceCall();
    
    if (showSuccess && successMessage && toastInstance) {
      toastInstance.success(successMessage, duration);
    }
    
    return result;
  } catch (error) {
    if (showError && toastInstance) {
      // Use errorMessage if provided, otherwise extract from error
      const message = errorMessage || getErrorMessage(error, 'An error occurred');
      toastInstance.error(message, duration);
    }
    throw error;
  }
};

/**
 * Show success toast
 */
export const showSuccess = (message, duration = 4000) => {
  if (toastInstance) {
    toastInstance.success(message, duration);
  }
};

/**
 * Show error toast
 * Automatically extracts readable message from error objects
 */
export const showError = (error, duration = 4000) => {
  if (toastInstance) {
    // Extract readable message from error (handles objects, strings, etc.)
    const message = getErrorMessage(error, 'An error occurred');
    toastInstance.error(message, duration);
  }
};

/**
 * Show warning toast
 */
export const showWarning = (message, duration = 4000) => {
  if (toastInstance) {
    toastInstance.warning(message, duration);
  }
};

/**
 * Show info toast
 */
export const showInfo = (message, duration = 4000) => {
  if (toastInstance) {
    toastInstance.info(message, duration);
  }
};
